import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { useBrandKit } from "hooks/useBrandKit/";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Templates } from "pages/media/components/storyboardEditor/types/";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "@refinedev/core";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { AUTOPILOT_STEPS_V2 } from "./constants";
import { StepItem } from "./StepItem";
import { PromptInsightsStep } from "./PromptInsightsStep";
import type { DocumentInsightAsset } from "./types";
import {
  ContentWrapper,
  HeadingBlack,
  HeadingRow,
  HeadingTeal,
  Page,
  Scroll,
  StickyHeader,
  WhiteCard,
} from "./Autopilot.styled";
import { FinalStep } from "./FinalStep";

interface AutopilotProps {
  projectId: string;
  sections: SectionWithSlides[];
  voiceoverSections: SectionWithSlides[];
  thinkingProcess: string[];
  templates: Templates;
  mediaAspectRatio: number;
  objectives: string[] | null;
  brandKitData: ReturnType<typeof useBrandKit>;
  isPublished?: boolean;
  isStoryboardError?: boolean;
  isPromptProject?: boolean;
  documentInsight?: DocumentInsightAsset;
  skipAnimations?: boolean;
}

// Prompt projects have an extra initial step (PromptInsights).
// We use PROMPT_INSIGHTS_INDEX = -1 so all regular step indices stay unchanged.
const PROMPT_INSIGHTS_INDEX = -1;

export const Autopilot = ({
  sections,
  voiceoverSections,
  projectId,
  thinkingProcess,
  objectives,
  brandKitData,
  templates,
  mediaAspectRatio,
  isPublished,
  isStoryboardError,
  isPromptProject,
  documentInsight,
  skipAnimations,
}: AutopilotProps) => {
  const t = useTranslate();
  const navigate = useNavigate();

  const { paywallFlow, setGraceProjectId, resetDismissed } = useSoftPaywall();

  // Reset dismiss flag so the soft paywall can show again for this new experience
  useEffect(() => {
    resetDismissed();
  }, [resetDismissed]);

  const [activeIndex, setActiveIndex] = useState(
    skipAnimations ? AUTOPILOT_STEPS_V2.length - 1 : 0
  );
  const [doneSet, setDoneSet] = useState<Set<number>>(
    skipAnimations ? new Set(AUTOPILOT_STEPS_V2.map((s) => s.id)) : new Set()
  );
  const [promptInsightsDone, setPromptInsightsDone] = useState(
    skipAnimations ? true : false
  );

  // When isPromptProject becomes known (after project data loads), reset to
  // the PromptInsights step if we haven't started progressing yet.
  const promptInitRef = useRef(false);
  if (
    isPromptProject &&
    !promptInitRef.current &&
    activeIndex === 0 &&
    doneSet.size === 0 &&
    !skipAnimations
  ) {
    promptInitRef.current = true;
    setActiveIndex(PROMPT_INSIGHTS_INDEX);
  }
  const stepRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);

  // Detect manual scrolling — disables auto-scroll until the next step transition
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const markUserScrolled = () => {
      userScrolledRef.current = true;
    };
    el.addEventListener("wheel", markUserScrolled, { passive: true });
    el.addEventListener("touchmove", markUserScrolled, { passive: true });
    return () => {
      el.removeEventListener("wheel", markUserScrolled);
      el.removeEventListener("touchmove", markUserScrolled);
    };
  }, []);

  useEffect(() => {
    if (skipAnimations) return;
    // Reset manual scroll flag on step transition
    userScrolledRef.current = false;
    const isLastStep = activeIndex >= AUTOPILOT_STEPS_V2.length - 1;
    const delay = isLastStep ? 1000 : 600;
    const t = setTimeout(() => {
      if (userScrolledRef.current) return;
      stepRefs.current.get(activeIndex)?.scrollIntoView({
        behavior: "smooth",
        block: isLastStep ? "start" : "center",
      });
    }, delay);
    return () => clearTimeout(t);
  }, [activeIndex, skipAnimations]);

  const autoScrollAllowed = useMemo(
    () => ({
      get current() {
        return !userScrolledRef.current;
      },
    }),
    []
  );

  const regularSteps = AUTOPILOT_STEPS_V2.slice(0, -1);
  const finalStep = AUTOPILOT_STEPS_V2[AUTOPILOT_STEPS_V2.length - 1];

  const handlePromptInsightsComplete = useCallback(() => {
    setPromptInsightsDone(true);
    // Only advance to step 0 if we're still on the PromptInsights step.
    // When skipAnimations is true, activeIndex is already past 0.
    setActiveIndex((prev) => (prev === PROMPT_INSIGHTS_INDEX ? 0 : prev));
  }, []);

  const handleStepComplete = useCallback((stepId: number, index: number) => {
    setDoneSet((p) => {
      if (p.has(stepId)) return p;
      return new Set([...p, stepId]);
    });
    setActiveIndex((prev) => Math.max(prev, index + 1));
  }, []);

  const handleFinalComplete = useCallback(() => {
    if (paywallFlow === "soft_3_exp") {
      setGraceProjectId(projectId);
    }
    navigate(`/media/projects/${projectId}`);
  }, [navigate, projectId, paywallFlow, setGraceProjectId]);

  return (
    <Page>
      <StickyHeader>
        <HeadingRow>
          <HeadingBlack>{t("media.autopilot.headingBlack")}</HeadingBlack>
          <HeadingTeal>{t("media.autopilot.headingTeal")}</HeadingTeal>
        </HeadingRow>
      </StickyHeader>
      <Scroll ref={scrollRef}>
        <WhiteCard>
          <ContentWrapper>
            {isPromptProject && (
              <PromptInsightsStep
                documentInsight={documentInsight}
                onComplete={handlePromptInsightsComplete}
                skipAnimations={skipAnimations}
                stepRef={(el) => {
                  if (el) stepRefs.current.set(PROMPT_INSIGHTS_INDEX, el);
                }}
              />
            )}

            {regularSteps.map((step, index) => {
              if (index > activeIndex) return null;
              return (
                <StepItem
                  key={step.id}
                  step={step}
                  index={index}
                  isActive={index === activeIndex}
                  thinkingProcess={thinkingProcess}
                  sections={sections}
                  voiceoverSections={voiceoverSections}
                  objectives={objectives}
                  brandKitData={brandKitData}
                  templates={templates}
                  mediaAspectRatio={mediaAspectRatio}
                  isDone={doneSet.has(step.id)}
                  isLast={false}
                  onComplete={() => handleStepComplete(step.id, index)}
                  isStoryboardError={isStoryboardError}
                  isPromptProject={isPromptProject}
                  autoScrollAllowed={autoScrollAllowed}
                  stepRef={(el) => {
                    if (el) stepRefs.current.set(index, el);
                  }}
                />
              );
            })}

            {activeIndex >= regularSteps.length && finalStep && (
              <FinalStep
                step={finalStep}
                index={regularSteps.length}
                isActive={
                  activeIndex === regularSteps.length &&
                  !doneSet.has(finalStep.id)
                }
                isDone={doneSet.has(finalStep.id)}
                onComplete={handleFinalComplete}
                sections={sections}
                templates={templates}
                brandKitData={brandKitData}
                mediaAspectRatio={mediaAspectRatio}
                isPublished={isPublished}
                skipAnimations={skipAnimations}
                stepRef={(el) => {
                  if (el) stepRefs.current.set(regularSteps.length, el);
                }}
              />
            )}

            <div style={{ height: 1 }} />
          </ContentWrapper>
        </WhiteCard>
      </Scroll>
    </Page>
  );
};
